import {
  BaseBookingModel,
  FoodBookingModel,
  ViewDailyNote,
  ViewFoodEvent,
} from '@xeetr/types/models'
import { format } from 'date-fns'
import { de } from 'date-fns/locale'
import { CalendarIcon, FileTextIcon, MapPinIcon } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { useTenant } from '@/hooks/useTenant'
import { cn } from '@/lib/utils'
import { aggregateFields } from '@/utils/monthlyOverview'

interface DayCardProps {
  day: Date
  bookings: BaseBookingModel[]
  events: ViewFoodEvent[]
  dailyNote?: ViewDailyNote
  visibleFields: Record<string, boolean>
}

export const DayCard = ({ day, bookings, events, dailyNote, visibleFields }: DayCardProps) => {
  const { tenant } = useTenant()

  // Find Catering field ID
  const cateringFieldId = tenant?.bookingCustomFields?.find(
    (field) => field.name === 'Catering'
  )?.id

  const aggregatedFields = aggregateFields(bookings, cateringFieldId)

  const hasActivity = bookings.length > 0 || events.length > 0 || dailyNote

  const cateringBookings = bookings.filter(
    (booking) =>
      cateringFieldId && booking.bookingAdditions?.customFields?.[cateringFieldId] === true
  )

  const formatCateringBookingLabel = (booking: FoodBookingModel) => {
    const fromLabel = format(booking.from, 'HH:mm')
    let customerLabel = `${booking.customer.firstName} ${booking.customer.lastName}`
    if (booking.customer.companyName) {
      customerLabel += ` (${booking.customer.companyName})`
    }
    const amountLabel = `${booking.amount} Personen`
    return (
      <>
        {fromLabel} - {customerLabel} - {amountLabel}
        {booking.bookingAdditions && booking.bookingAdditions.additionalInfo && (
          <div className="mt-1 flex items-center text-xs">
            <FileTextIcon className="mr-1 h-4 w-4" /> {booking.bookingAdditions.additionalInfo}
          </div>
        )}
      </>
    )
  }

  return (
    <Card
      className={cn({
        'border-primary/20 bg-primary/5': hasActivity,
      })}
    >
      <CardHeader className="p-2 px-4">
        <CardTitle className="flex items-center gap-2 text-sm font-medium">
          <div>
            {format(day, 'dd.MM.', { locale: de })}
            <span className="text-muted-foreground ml-1 text-xs">
              {format(day, 'EEE', { locale: de })}
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {Object.entries(aggregatedFields)
              .filter(([fieldName]) => visibleFields[fieldName] ?? true)
              .sort(([fieldNameA], [fieldNameB]) => {
                const fieldOrder = ['Personen', 'Tische']
                const indexA = fieldOrder.indexOf(fieldNameA)
                const indexB = fieldOrder.indexOf(fieldNameB)
                return indexA - indexB
              })
              .map(([fieldName, data]) => {
                const typedData = data as { count: number; values: Set<string>; sum?: number }
                const displayName = fieldName
                let displayValue: string | number

                if (fieldName === 'Personen' && typedData.sum !== undefined) {
                  displayValue = typedData.sum
                } else if (fieldName === 'Tische') {
                  displayValue = typedData.values.size
                } else {
                  displayValue =
                    typedData.values.size === 1 ? Array.from(typedData.values)[0] : typedData.count
                }

                return (
                  <Badge key={fieldName} variant="secondary" className="w-fit text-xs font-medium">
                    {displayName}: {displayValue}
                  </Badge>
                )
              })}

            {bookings.length > 0 && (
              <Badge variant="outline" className="w-fit text-xs">
                {bookings.length} Buchung{bookings.length !== 1 ? 'en' : ''}
              </Badge>
            )}

            {events.length > 0 && (
              <Badge variant="outline" className="w-fit text-xs opacity-75">
                {events.length} Event{events.length !== 1 ? 's' : ''}
              </Badge>
            )}
          </div>
        </CardTitle>
      </CardHeader>
      {(cateringBookings.length > 0 || dailyNote || events.length > 0) && (
        <CardContent className="px-4 pb-2 text-xs">
          <div className="flex items-center gap-2 pt-1">
            {dailyNote && (
              <Badge
                variant="outline"
                className="text-muted-foreground border-primary flex w-fit flex-wrap items-center gap-1 text-balance p-2 text-xs"
              >
                <FileTextIcon className="mr-1 h-4 w-4" />
                {dailyNote.note.split(' ').map((word, index) => (
                  <span
                    key={index}
                    dangerouslySetInnerHTML={{
                      __html: word + (index < dailyNote.note.split(' ').length - 1 ? ' ' : ''),
                    }}
                  ></span>
                ))}
              </Badge>
            )}
            {cateringBookings.map((booking) => (
              <Badge
                variant="outline"
                key={booking.id}
                className="text-muted-foreground flex w-fit flex-col items-start p-2 text-xs"
              >
                {formatCateringBookingLabel(booking as FoodBookingModel)}
              </Badge>
            ))}
            {events.map((event) => (
              <Badge
                variant="outline"
                key={event.id}
                className="flex w-fit flex-col items-start border-orange-300 p-2 text-xs"
              >
                <div className="flex items-center gap-1 font-medium">
                  <CalendarIcon className="h-3 w-3" />
                  {event.name}
                </div>
                <div className="mt-1 text-xs opacity-75">
                  {format(new Date(event.from), 'HH:mm')} - {format(new Date(event.until), 'HH:mm')}
                </div>
                {event.description && (
                  <div className="mt-1 text-xs opacity-75">{event.description}</div>
                )}
                {event.rooms && event.rooms.length > 0 && (
                  <div className="mt-1 flex items-center text-xs opacity-75">
                    <MapPinIcon className="mr-1 h-3 w-3" />
                    {event.rooms.map((r) => r.name).join(', ')}
                  </div>
                )}
              </Badge>
            ))}
          </div>
        </CardContent>
      )}
    </Card>
  )
}
